/**
 * Language context — wrap react-i18next, expose t() và changeLanguage.
 * Lưu ngôn ngữ đang chọn vào localStorage để reload giữ nguyên.
 */

import * as React from "react";
import { useTranslation } from "react-i18next";
import { LANGUAGES, type Lang } from "@/app/i18n/types";

const STORAGE_KEY = "lumina_mkt_lang";

type LanguageContextValue = {
  lang: Lang;
  setLang: (l: Lang) => void;
};

const LanguageContext = React.createContext<LanguageContextValue | null>(null);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation();

  const setLang = React.useCallback((l: Lang) => {
    i18n.changeLanguage(l);
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      /* ignore */
    }
    document.documentElement.lang = l;
  }, [i18n]);

  const lang = (i18n.resolvedLanguage ?? i18n.language ?? "vi") as Lang;
  React.useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  // Sanity: nếu resolved không nằm trong LANGUAGES (vd SSR/test) → fallback 'vi'.
  const safeLang: Lang = (LANGUAGES as readonly string[]).includes(lang)
    ? lang
    : "vi";

  const value = React.useMemo<LanguageContextValue>(
    () => ({ lang: safeLang, setLang }),
    [safeLang, setLang]
  );

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextValue {
  const ctx = React.useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
}
